import { Popover } from "@base-ui-components/react/popover"
import cx from "classnames"
import {
    AlertTriangle,
    ArchiveRestore,
    Check,
    CircleAlert,
    Files,
    FolderGit2,
    GitBranch,
    GitCommitHorizontal,
    GitPullRequest,
    Loader2,
    MoreHorizontal,
    Plus,
    RefreshCw,
    X,
} from "lucide-react"
import { type ReactNode, useEffect, useState } from "react"
import { usePortalContainer } from "../hooks/usePortalContainer"
import type { GitSummaryResponse } from "../nestAPIs/git"
import type { TaskPullRequestAutomation } from "../persistence/records"
import { openExternalUrl } from "../platform/actions"
import type { TaskModel } from "../store/TaskModel"
import type { WildLarkPullRequestSnapshot } from "../wildlark/client"
import type { TaskCheckoutSummary, TaskCheckoutSummaryReadStats } from "../store/taskCheckouts"
import type { DesignatedPathState, ManualPathState, TaskDirectorySelectionMode, TaskDirectoryTarget } from "../store/taskDirectory"
import { copyTextToClipboard } from "../utils/clipboard"
import { filterGitSummary } from "../utils/gitTestFileFilter"
import { GridDisclosure } from "./sidebar/GridDisclosure"
import { PullRequestAutomationControls } from "./PullRequestAutomationControls"
import { Menu, type MenuItem } from "./ui"

export interface TaskWorktreeControl {
    target: TaskDirectoryTarget
    path: string
    originalPath: string
    selectionMode: TaskDirectorySelectionMode
    manualPath?: string
    manualPathState: ManualPathState
    designatedPath?: string
    designatedPathState: DesignatedPathState
    checkoutSummariesEnabled?: boolean
    checkoutSummaries?: TaskCheckoutSummary[]
    checkoutSummaryBaseBranch?: string | null
    checkoutSummaryReads?: TaskCheckoutSummaryReadStats | null
    checkoutSummariesError?: string | null
    recovery?: {
        backupCommit: string
        requested: number
        succeeded: number
    }
}

export interface TaskPullRequestControl {
    id?: string
    url: string
    number?: number
    provider: "github" | "gitlab" | "other"
    watch?: "active" | "unsupported"
}

export type ExternalWatchPresentationStatus = "idle" | "watching" | "degraded"

export function LinkedRequestControl({
    pullRequest,
    snapshot,
    watchStatus,
    automation,
    automationDisabledReason,
    onWatchChange,
    onRetryAutomation,
    onRefresh,
    onRemove,
}: {
    pullRequest: TaskPullRequestControl
    snapshot?: WildLarkPullRequestSnapshot
    watchStatus?: ExternalWatchPresentationStatus
    automation?: TaskPullRequestAutomation
    automationDisabledReason?: string
    onWatchChange?: (enabled: boolean) => Promise<string | null>
    onRetryAutomation?: () => Promise<string | null>
    onRefresh?: () => Promise<string | null>
    onRemove?: () => Promise<string | null>
}): JSX.Element {
    const [removePending, setRemovePending] = useState(false)
    const [refreshPending, setRefreshPending] = useState(false)
    const [removeError, setRemoveError] = useState<string | null>(null)
    const [waitError, setWaitError] = useState<string | null>(null)
    const stateLabel = pullRequestStateLabel(snapshot, Boolean(automation))
    const watcherLabel =
        pullRequest.watch === "unsupported" ? "Automatic watching unavailable" : watchStatus === "degraded" ? "Watcher degraded" : "Watcher healthy"
    const attentionNeeded =
        snapshot?.state === "closed" ||
        snapshot?.checks === "failing" ||
        snapshot?.reviewDecision === "changes_requested" ||
        snapshot?.mergeStateStatus === "dirty" ||
        snapshot?.mergeStateStatus === "behind" ||
        watchStatus === "degraded"
    const complete = snapshot?.state === "merged" || stateLabel === "Ready to merge" || snapshot?.reviewDecision === "approved"
    const stateClassName = attentionNeeded ? "text-warning" : complete ? "text-success" : "text-muted"
    const terminal = snapshot?.state === "merged" || snapshot?.state === "closed"
    const remove = async () => {
        if (!onRemove || removePending) return
        setRemovePending(true)
        setRemoveError(null)
        try {
            setRemoveError(await onRemove())
        } finally {
            setRemovePending(false)
        }
    }
    const refresh = async () => {
        if (!onRefresh || refreshPending) return
        setRefreshPending(true)
        setWaitError(null)
        try {
            setWaitError(await onRefresh())
        } finally {
            setRefreshPending(false)
        }
    }
    const menuItems: MenuItem[] = onRemove
        ? [
              {
                  id: "unlink-pull-request",
                  label: (
                      <span className="flex items-center gap-2">
                          <X size={14} />
                          Unlink pull request
                      </span>
                  ),
                  onSelect: remove,
              } satisfies MenuItem,
          ]
        : []
    const operationError = waitError ?? removeError
    const requestLabel = `${pullRequest.provider === "gitlab" ? "MR" : "PR"} ${pullRequest.number ? `#${pullRequest.number}` : "request"}`
    return (
        <div className="w-full bg-base-100 px-2.5 py-2" role="group" aria-label={`Linked ${requestLabel}`}>
            <div className="flex min-w-0 flex-wrap items-center gap-1">
                <button
                    type="button"
                    onClick={() => void openExternalUrl(pullRequest.url)}
                    className="btn flex h-6 shrink-0 items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                    title={`${pullRequest.url}\n${stateLabel}\n${watcherLabel}`}
                    aria-label={`Open ${requestLabel}: ${snapshot?.title ?? stateLabel}`}
                >
                    <GitPullRequest size={12} className="text-muted" />
                    {requestLabel}
                </button>
                <span className="flex-1" aria-hidden="true" />
                {onWatchChange && (automation || (pullRequest.provider === "github" && pullRequest.watch === "active")) && (
                    <PullRequestAutomationControls
                        automation={automation}
                        disabledReason={automationDisabledReason ?? (!automation && terminal ? "This pull request is no longer open." : undefined)}
                        onChange={onWatchChange}
                        onRetry={onRetryAutomation}
                    />
                )}
                {onRefresh && (
                    <button
                        type="button"
                        onClick={() => void refresh()}
                        className="btn flex h-6 w-6 shrink-0 items-center justify-center text-muted hover:bg-base-200 hover:text-base-content disabled:opacity-40"
                        disabled={refreshPending}
                        aria-label="Refresh pull request"
                        title={waitError ?? "Refresh pull request state"}
                    >
                        <RefreshCw size={13} className={cx(refreshPending && "animate-spin")} />
                    </button>
                )}
                {pullRequest.id && menuItems.length > 0 && (
                    <Menu
                        trigger={
                            removePending ? (
                                <Loader2 size={13} className="animate-spin" />
                            ) : removeError ? (
                                <CircleAlert size={13} className="text-warning" />
                            ) : (
                                <MoreHorizontal size={15} />
                            )
                        }
                        triggerAriaLabel="Pull request actions"
                        triggerTitle={removeError ?? "Pull request actions"}
                        sections={[{ items: menuItems }]}
                        disabled={removePending}
                        side="bottom"
                        align="end"
                        sideOffset={4}
                        className={{
                            trigger: "!h-6 !w-6 !rounded-none !border-0 !bg-transparent !p-0 text-muted hover:!bg-base-200 hover:text-base-content",
                            popup: "min-w-48 !rounded-none",
                        }}
                    />
                )}
            </div>
            {snapshot?.title && (
                <div className="mt-1 truncate text-[11px] leading-4 text-muted" title={snapshot.title}>
                    {snapshot.title}
                </div>
            )}
            <div className={cx("mt-0.5 flex items-center gap-1 text-[10px] leading-4", stateClassName)}>
                {watchStatus === "degraded" && <CircleAlert size={11} />}
                {stateLabel}
                {watchStatus === "degraded" && <span className="text-muted">· Watcher degraded</span>}
            </div>
            {operationError && (
                <div className="flex items-start gap-1.5 border-t border-border bg-warning/10 px-2 py-1.5 text-[10px] leading-4 text-warning" role="alert">
                    <CircleAlert size={12} className="mt-0.5 shrink-0" />
                    <span className="min-w-0 break-words">{operationError}</span>
                </div>
            )}
        </div>
    )
}

export function pullRequestStateLabel(snapshot: WildLarkPullRequestSnapshot | undefined, waiting: boolean): string {
    if (!snapshot) return waiting ? "Waiting for review" : "State loading"
    if (snapshot.state === "merged") return "Merged"
    if (snapshot.state === "closed") return "Closed"
    if (snapshot.draft) return "Draft"
    if (snapshot.mergeStateStatus === "dirty") return "Conflicts"
    if (snapshot.mergeStateStatus === "behind") return "Branch behind"
    if (snapshot.mergeStateStatus === "blocked") return "Merge blocked"
    if (snapshot.reviewDecision === "changes_requested") return "Changes requested"
    if (snapshot.reviewDecision === "approved") {
        if (snapshot.checks === "failing") return "Approved · checks failing"
        if (snapshot.checks === "pending") return "Approved · checks pending"
        return snapshot.mergeStateStatus === "clean" ? "Ready to merge" : "Approved"
    }
    if (snapshot.checks === "failing") return "Checks failing"
    if (snapshot.reviewDecision === "not_required") {
        if (snapshot.checks === "pending") return "Checks pending"
        if (
            (snapshot.checks === "passing" || snapshot.checks === "none") &&
            (snapshot.mergeStateStatus === "clean" || snapshot.mergeStateStatus === "has_hooks")
        )
            return "Ready to merge"
        return "Open"
    }
    return waiting ? "Waiting for review" : "Open"
}

export function taskWorktreeControlForModel(taskModel: TaskModel): TaskWorktreeControl {
    const workingDirectory = taskModel.getDirectory("working")
    const originalDirectory = taskModel.getDirectory("original")
    return {
        target: workingDirectory?.target ?? "primary",
        path: workingDirectory?.path ?? originalDirectory?.path ?? "",
        originalPath: originalDirectory?.path ?? "",
        selectionMode: taskModel.directoryView?.selectionMode ?? (taskModel.directorySelection ? "manual" : "automatic"),
        manualPath: taskModel.directoryView?.manualPath ?? taskModel.directorySelection?.path,
        manualPathState: taskModel.directoryView?.manualPathState ?? "none",
        designatedPath: taskModel.directoryView?.designatedPath,
        designatedPathState: taskModel.directoryView?.designatedPathState ?? "none",
        checkoutSummariesEnabled: true,
        checkoutSummaries: taskModel.checkoutSummaries,
        checkoutSummaryBaseBranch: taskModel.checkoutSummaryBaseBranch,
        checkoutSummaryReads: taskModel.checkoutSummaryReads,
        checkoutSummariesError: taskModel.checkoutSummariesError,
        recovery: taskModel.worktreeRecovery,
    }
}

function sourceLabel(target: TaskDirectoryTarget): string {
    if (target === "agent-worktree") return "Agent worktree"
    if (target === "manual") return "Custom"
    return "Main"
}

function designatedStateLabel(state: DesignatedPathState): string {
    switch (state) {
        case "active":
            return "In use"
        case "inactive":
            return "Unused"
        case "absent":
            return "Not created"
        case "none":
            return ""
    }
}

export function TaskGitControl({
    gitStatus,
    worktree,
    onDirectoryChange,
    onCheckoutSummariesOpen,
    onDirectoryActivated,
    onChooseDirectory,
    directoryPickerPending = false,
    excludeTestFiles = false,
    touchFriendly = false,
    iconOnly = false,
    side = "top",
    align = "end",
}: {
    gitStatus?: GitSummaryResponse | null
    worktree?: TaskWorktreeControl
    onDirectoryChange?: (path: string | null) => Promise<string | null>
    onCheckoutSummariesOpen?: () => void
    onDirectoryActivated?: () => void
    onChooseDirectory?: () => void
    directoryPickerPending?: boolean
    excludeTestFiles?: boolean
    touchFriendly?: boolean
    iconOnly?: boolean
    side?: "top" | "bottom" | "left" | "right"
    align?: "start" | "center" | "end"
}) {
    const portalContainer = usePortalContainer()
    const target = worktree?.target ?? "primary"
    const displayBranch = (gitStatus?.branch ?? (gitStatus?.headCommit ? `Detached at ${gitStatus.headCommit.slice(0, 7)}` : "Checkout")).replace(
        /^wildlark\//,
        ""
    )
    const triggerLabel = `${sourceLabel(target)} · ${displayBranch}`

    return (
        <Popover.Root onOpenChange={(open) => open && onCheckoutSummariesOpen?.()}>
            <Popover.Trigger
                className={cx(
                    "btn flex shrink-0 items-center gap-1.5 font-mono text-muted transition-colors hover:bg-base-200 hover:text-base-content",
                    touchFriendly ? "h-10 border border-border px-3 text-sm" : iconOnly ? "h-9 w-9 justify-center p-0" : "px-2 py-1 text-xs"
                )}
                title={`${sourceLabel(target)}: ${gitStatus?.branch ?? "Detached checkout"}`}
                aria-label={`Working directory ${triggerLabel}`}
            >
                {target !== "primary" ? <FolderGit2 size={touchFriendly ? 16 : 12} /> : <GitBranch size={touchFriendly ? 16 : 12} />}
                {!iconOnly && <span className={cx("truncate", touchFriendly ? "max-w-[220px]" : "max-w-[180px]")}>{triggerLabel}</span>}
            </Popover.Trigger>
            <Popover.Portal container={portalContainer}>
                <Popover.Positioner sideOffset={8} side={side} align={align} collisionPadding={8}>
                    <Popover.Popup className="z-50 w-[min(28rem,calc(100vw-1rem))] border border-border bg-base-100 shadow-lg outline-none">
                        <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
                            <div className="min-w-0">
                                <div className="text-xs font-medium text-base-content">Task checkouts</div>
                                <div className="truncate font-mono text-xs text-muted" title={gitStatus?.branch ?? gitStatus?.headCommit}>
                                    {gitStatus?.branch ?? (gitStatus?.headCommit ? `Detached at ${gitStatus.headCommit.slice(0, 7)}` : "Status unavailable")}
                                </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                                {onChooseDirectory && (
                                    <button
                                        type="button"
                                        className="btn flex h-7 w-7 items-center justify-center text-muted hover:bg-base-200 hover:text-base-content disabled:opacity-50"
                                        onClick={onChooseDirectory}
                                        disabled={directoryPickerPending}
                                        aria-label="Choose directory"
                                        title="Add or choose a checkout"
                                    >
                                        {directoryPickerPending ? <Loader2 size={12} className="animate-spin" /> : <Plus size={13} />}
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="p-3">
                            <TaskDirectorySelector
                                worktree={worktree}
                                onDirectoryChange={onDirectoryChange}
                                onDirectoryActivated={onDirectoryActivated}
                                excludeTestFiles={excludeTestFiles}
                            />
                        </div>
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    )
}

export function TaskDirectorySelector({
    worktree,
    onDirectoryChange,
    onDirectoryActivated,
    excludeTestFiles = false,
    narrow = false,
    renderActiveDirectoryContent,
}: {
    worktree?: TaskWorktreeControl
    onDirectoryChange?: (path: string | null) => Promise<string | null>
    onDirectoryActivated?: () => void
    excludeTestFiles?: boolean
    narrow?: boolean
    renderActiveDirectoryContent?: () => ReactNode
}): JSX.Element {
    const [selectionPending, setSelectionPending] = useState(false)
    const [selectionError, setSelectionError] = useState<string | null>(null)
    const manualPath = worktree?.manualPath
    const manualUsesMain = worktree?.selectionMode === "manual" && manualPath === worktree.originalPath
    const manualUsesDesignated = worktree?.selectionMode === "manual" && !!manualPath && manualPath === worktree.designatedPath
    const manualUsesCustom = worktree?.selectionMode === "manual" && !manualUsesMain && !manualUsesDesignated
    const checkoutSummaries = (worktree?.checkoutSummaries ?? [])
        .filter((checkout) => checkout.status !== null)
        .sort((left, right) => Number(!!left.auxiliaryLabel) - Number(!!right.auxiliaryLabel))
    const selectedCheckoutIsVisible = checkoutSummaries.some((checkout) => worktree && sameDirectoryPath(checkout.path, worktree.path))

    const applyDirectory = async (path: string | null) => {
        if (!onDirectoryChange || selectionPending) return
        setSelectionPending(true)
        setSelectionError(null)
        let activated = false
        try {
            const error = await onDirectoryChange(path)
            setSelectionError(error)
            activated = !error
        } catch (error) {
            console.error("[TaskDirectorySelector] Failed to change task directory:", error)
            setSelectionError("The working directory could not be changed.")
        } finally {
            setSelectionPending(false)
        }
        if (activated) onDirectoryActivated?.()
    }

    return (
        <div className={cx("flex flex-col", narrow ? "gap-0" : "gap-3")}>
            {!narrow && (
                <div>
                    <div className="text-xs font-medium text-base-content">Rendering from</div>
                    <div className={cx("mt-1 font-mono text-xs text-muted", narrow ? "truncate" : "break-all")} title={worktree?.path}>
                        {worktree?.path ?? "Directory unavailable"}
                    </div>
                </div>
            )}
            {worktree && onDirectoryChange && worktree.checkoutSummariesEnabled && (
                <div>
                    {!narrow && <div className="mb-1.5 text-[11px] text-muted">Associated checkouts</div>}
                    <div className={cx(narrow ? "border-t border-border" : "border border-border")}>
                        {checkoutSummaries.map((checkout, index) => {
                            const selected = sameDirectoryPath(checkout.path, worktree.path)
                            return (
                                <div key={checkout.path} className="border-b border-border last:border-b-0">
                                    {checkout.auxiliaryLabel && !checkoutSummaries[index - 1]?.auxiliaryLabel && (
                                        <div role="heading" aria-level={3} className="border-b border-border px-3 py-1.5 text-[11px] text-muted">
                                            Auxiliary checkouts
                                        </div>
                                    )}
                                    <div className={cx("flex items-center", selected && "bg-primary/10")}>
                                        <button
                                            type="button"
                                            className={cx(
                                                "btn min-w-0 flex-1 px-2.5 py-2 text-left transition-colors hover:bg-base-200 disabled:opacity-60",
                                                narrow
                                                    ? "grid min-h-9 grid-cols-[1rem_minmax(0,1fr)_auto] items-center gap-1.5 pl-3 pr-1 py-1.5"
                                                    : "grid grid-cols-[1rem_minmax(0,1fr)_auto] items-center gap-2 pr-1"
                                            )}
                                            onClick={() => {
                                                if (selected) {
                                                    onDirectoryActivated?.()
                                                    return
                                                }
                                                void applyDirectory(checkout.path)
                                            }}
                                            disabled={selectionPending || !checkout.status}
                                            aria-pressed={selected}
                                            title={checkout.error ?? checkout.path}
                                        >
                                            <span className="flex h-4 w-4 items-center justify-center">
                                                {selected && <Check size={13} className="text-primary" />}
                                            </span>
                                            <span className="min-w-0">
                                                <span className="flex min-w-0 items-center gap-1">
                                                    <CheckoutKind isWorktree={!checkout.isPrimary} />
                                                    <span className="truncate font-mono text-xs font-medium text-base-content">
                                                        {checkout.auxiliaryLabel ??
                                                            checkout.branch ??
                                                            (checkout.headCommit
                                                                ? `Detached at ${checkout.headCommit.slice(0, 7)}`
                                                                : checkout.isPrimary
                                                                  ? "Main"
                                                                  : checkout.isDesignated
                                                                    ? "Agent"
                                                                    : checkoutPathLabel(checkout.path))}
                                                    </span>
                                                </span>
                                                {checkout.auxiliaryLabel ? (
                                                    <span className="block truncate pl-5 text-[10px] leading-4 text-muted">
                                                        {checkout.branch ??
                                                            (checkout.headCommit ? `Snapshot at ${checkout.headCommit.slice(0, 7)}` : "Commit unavailable")}
                                                    </span>
                                                ) : (
                                                    !narrow && (
                                                        <span className="block truncate font-mono text-[10px] leading-4 text-muted" title={checkout.path}>
                                                            {checkout.path}
                                                        </span>
                                                    )
                                                )}
                                            </span>
                                            <span>
                                                <CheckoutMetrics
                                                    checkout={checkout}
                                                    baseBranch={worktree.checkoutSummaryBaseBranch}
                                                    excludeTestFiles={excludeTestFiles}
                                                    dense={narrow}
                                                />
                                            </span>
                                        </button>
                                        <CheckoutActions checkout={checkout} narrow={narrow} />
                                    </div>
                                    {renderActiveDirectoryContent && (
                                        <GridDisclosure
                                            open={selected}
                                            renderContent={() => (
                                                <div className={cx("border-l border-border", narrow ? "ml-8 pl-1.5 pr-1.5" : "ml-9 pl-2 pr-2")}>
                                                    {renderActiveDirectoryContent()}
                                                </div>
                                            )}
                                        />
                                    )}
                                </div>
                            )
                        })}
                        {renderActiveDirectoryContent && (
                            <GridDisclosure
                                open={!selectedCheckoutIsVisible}
                                renderContent={() => (
                                    <div className={cx("border-l border-border", narrow ? "ml-8 pl-1.5 pr-1.5" : "ml-9 pl-2 pr-2")}>
                                        {renderActiveDirectoryContent()}
                                    </div>
                                )}
                            />
                        )}
                    </div>
                    {!narrow && worktree.checkoutSummaryReads && worktree.checkoutSummaryReads.failed > 0 && (
                        <div
                            className="mt-2 flex items-start gap-1.5 text-[11px] leading-4 text-warning"
                            title={worktree.checkoutSummaryReads.firstErrors.join("\n")}
                        >
                            <CircleAlert size={12} className="mt-0.5 shrink-0" />
                            <span>
                                {worktree.checkoutSummaryReads.succeeded}/{worktree.checkoutSummaryReads.requested} loaded · done with gaps
                            </span>
                        </div>
                    )}
                    {worktree.checkoutSummariesError && <div className="mt-2 text-xs text-error">{worktree.checkoutSummariesError}</div>}
                </div>
            )}
            {worktree && onDirectoryChange && !worktree.checkoutSummariesEnabled && (
                <div className="border border-border">
                    <button
                        type="button"
                        className="btn flex w-full items-start gap-2 border-b border-border px-2.5 py-2 text-left hover:bg-base-200 disabled:opacity-50"
                        onClick={() => void applyDirectory(worktree.originalPath)}
                        disabled={selectionPending}
                        aria-pressed={manualUsesMain}
                    >
                        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">{manualUsesMain && <Check size={13} />}</span>
                        <span className="min-w-0">
                            <span className="block text-xs font-medium text-base-content">Main checkout</span>
                            <span className="block truncate font-mono text-xs text-muted" title={worktree.originalPath}>
                                {worktree.originalPath}
                            </span>
                        </span>
                    </button>
                    {worktree.designatedPath && (
                        <button
                            type="button"
                            className="btn flex w-full items-start gap-2 border-b border-border px-2.5 py-2 text-left hover:bg-base-200 disabled:opacity-50"
                            onClick={() => void applyDirectory(worktree.designatedPath ?? null)}
                            disabled={selectionPending || worktree.designatedPathState === "absent"}
                            aria-pressed={manualUsesDesignated}
                        >
                            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">{manualUsesDesignated && <Check size={13} />}</span>
                            <span className="min-w-0 flex-1">
                                <span className="flex items-center justify-between gap-2 text-xs">
                                    <span className="font-medium text-base-content">Agent checkout</span>
                                    <span className={worktree.designatedPathState === "active" ? "text-success" : "text-muted"}>
                                        {designatedStateLabel(worktree.designatedPathState)}
                                    </span>
                                </span>
                                <span className="block truncate font-mono text-xs text-muted" title={worktree.designatedPath}>
                                    {worktree.designatedPath}
                                </span>
                            </span>
                        </button>
                    )}
                    {manualUsesCustom && manualPath && (
                        <div className="flex items-start gap-2 border-b border-border bg-base-200 px-2.5 py-2">
                            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                                <Check size={13} />
                            </span>
                            <span className="min-w-0">
                                <span className="block text-xs font-medium text-base-content">Custom directory</span>
                                <span className="block truncate font-mono text-xs text-muted" title={manualPath}>
                                    {manualPath}
                                </span>
                            </span>
                        </div>
                    )}
                </div>
            )}
            {worktree?.manualPathState === "unavailable" && manualPath && (
                <div className="border border-warning/50 bg-warning/10 p-2 text-xs text-base-content">
                    <div className="font-medium">Manual directory unavailable</div>
                    <div className="mt-1 break-all font-mono text-muted">{manualPath}</div>
                    <div className="mt-1 text-muted">Task tools are temporarily using the main checkout.</div>
                </div>
            )}
            {selectionError && <div className="border border-error/50 bg-error/10 p-2 text-xs text-error">{selectionError}</div>}
            {worktree?.recovery && (
                <div className="flex items-center gap-2 border-t border-border pt-2 text-xs text-muted" title={worktree.recovery.backupCommit}>
                    <ArchiveRestore size={13} className="shrink-0" />
                    <span>Legacy recovery stash</span>
                    <span className="font-mono text-base-content">{worktree.recovery.backupCommit.slice(0, 7)}</span>
                    <span>
                        {worktree.recovery.requested} {worktree.recovery.requested === 1 ? "file" : "files"}
                    </span>
                </div>
            )}
        </div>
    )
}

function sameDirectoryPath(left: string, right: string): boolean {
    const normalize = (path: string) => path.replace(/\\/g, "/").replace(/\/+/g, "/").replace(/\/+$/g, "")
    return normalize(left) === normalize(right)
}

function checkoutPathLabel(path: string): string {
    const normalized = path.replace(/\\/g, "/").replace(/\/+$/g, "")
    return normalized.split("/").at(-1) || path
}

function CheckoutKind({ isWorktree }: { isWorktree: boolean }): JSX.Element {
    const label = isWorktree ? "Worktree" : "Branch"
    const Icon = isWorktree ? FolderGit2 : GitBranch
    return (
        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-muted" role="img" aria-label={`${label} checkout`} title={`${label} checkout`}>
            <Icon size={12} aria-hidden="true" />
        </span>
    )
}

function CheckoutActions({ checkout, narrow }: { checkout: TaskCheckoutSummary; narrow: boolean }): JSX.Element {
    const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle")
    const refLabel = checkout.branch ?? (checkout.headCommit ? `Detached at ${checkout.headCommit.slice(0, 7)}` : checkoutPathLabel(checkout.path))

    useEffect(() => {
        if (copyState === "idle") return
        const timer = window.setTimeout(() => setCopyState("idle"), 1_500)
        return () => window.clearTimeout(timer)
    }, [copyState])

    const copy = async (value: string) => {
        try {
            await copyTextToClipboard(value)
            setCopyState("copied")
        } catch (error) {
            console.error("[TaskDirectorySelector] Failed to copy checkout detail:", error)
            setCopyState("error")
        }
    }

    const items: MenuItem[] = [
        ...(checkout.branch
            ? [
                  {
                      id: "copy-branch-name",
                      label: (
                          <span className="flex items-center gap-2">
                              <GitBranch size={15} />
                              Copy branch name
                          </span>
                      ),
                      onSelect: () => copy(checkout.branch ?? ""),
                  } satisfies MenuItem,
              ]
            : []),
        {
            id: "copy-checkout-path",
            label: (
                <span className="flex items-center gap-2">
                    <FolderGit2 size={15} />
                    Copy checkout path
                </span>
            ),
            onSelect: () => copy(checkout.path),
        },
        ...(checkout.headCommit
            ? [
                  {
                      id: "copy-commit-sha",
                      label: (
                          <span className="flex items-center gap-2">
                              <GitCommitHorizontal size={15} />
                              Copy commit SHA
                          </span>
                      ),
                      onSelect: () => copy(checkout.headCommit),
                  } satisfies MenuItem,
              ]
            : []),
    ]

    return (
        <span className="relative shrink-0 pr-1" data-copy-state={copyState}>
            <Menu
                trigger={
                    copyState === "copied" ? (
                        <Check size={13} className="text-success" />
                    ) : copyState === "error" ? (
                        <AlertTriangle size={13} className="text-error" />
                    ) : (
                        <MoreHorizontal size={14} />
                    )
                }
                triggerAriaLabel={`Checkout actions for ${refLabel}`}
                triggerTitle={copyState === "error" ? "Copy failed" : undefined}
                sections={[{ items }]}
                side="bottom"
                align="end"
                sideOffset={4}
                className={{
                    trigger: `${narrow ? "!h-7 !w-6" : "!h-8 !w-7"} !rounded-none !border-0 !bg-transparent !p-0 text-muted hover:!bg-base-300 hover:text-base-content`,
                    popup: "min-w-52",
                }}
            />
            {copyState === "error" && (
                <span className="sr-only" role="status">
                    Copy failed. Try again.
                </span>
            )}
        </span>
    )
}

function CheckoutMetrics({
    checkout,
    baseBranch,
    excludeTestFiles = false,
    dense = false,
}: {
    checkout: TaskCheckoutSummary
    baseBranch?: string | null
    excludeTestFiles?: boolean
    dense?: boolean
}): JSX.Element {
    const status = checkout.status ? filterGitSummary(checkout.status, excludeTestFiles) : null
    const filesChanged = status?.total.filesChanged
    const insertions = status?.total.insertions
    const deletions = status?.total.deletions
    const unknownFiles = status?.total.unknownFiles ?? 0
    const linesUnavailable = !!status?.hasChanges && insertions === 0 && deletions === 0
    const commitsAhead = checkout.commitsAheadOfBase
    const clean = commitsAhead === 0 && status !== null && filesChanged === 0 && insertions === 0 && deletions === 0 && unknownFiles === 0 && !status.hasChanges
    return (
        <span className={cx("flex shrink-0 items-center font-mono tabular-nums text-muted", dense ? "gap-1.5 text-[9px]" : "gap-2 text-[10px]")}>
            {clean ? (
                <span title={`No commits ahead of ${baseBranch ?? "base"} and no working-tree changes`}>clean</span>
            ) : (
                <>
                    {commitsAhead === null ? (
                        <span className="flex items-center gap-0.5" title={`Unknown commits ahead of ${baseBranch ?? "base"}`}>
                            <GitCommitHorizontal size={10} />?
                        </span>
                    ) : commitsAhead > 0 ? (
                        <span className="flex items-center gap-0.5" title={`${commitsAhead} commits ahead of ${baseBranch ?? "base"}`}>
                            <GitCommitHorizontal size={10} />
                            {commitsAhead}
                        </span>
                    ) : null}
                    {filesChanged === undefined ? (
                        <span className="flex items-center gap-0.5" title="Unknown changed files">
                            <Files size={10} />?
                        </span>
                    ) : filesChanged > 0 ? (
                        <span className="flex items-center gap-0.5" title={`${filesChanged} changed files`}>
                            <Files size={10} />
                            {filesChanged}
                        </span>
                    ) : null}
                    {status ? (
                        linesUnavailable ? (
                            <span className="text-primary" title={unknownFiles > 0 ? `Line totals unavailable for ${unknownFiles} files` : "Non-line changes"}>
                                dirty
                            </span>
                        ) : (
                            <span className="flex items-center gap-1">
                                {!!insertions && <span className="text-success">+{insertions}</span>}
                                {!!deletions && <span className="text-error">−{deletions}</span>}
                                {unknownFiles > 0 && <span>+?</span>}
                            </span>
                        )
                    ) : (
                        <span title="Summary unavailable">—</span>
                    )}
                </>
            )}
        </span>
    )
}
